import { Container, Row, Col, ListGroupItem, ListGroup, Button as BsButton, Modal } from "react-bootstrap";
import { Button } from "./Controls/Button";
import { useState } from "react";
import { AddLineItemModal } from "./FacilityCustomOrderCreatePageAddLineItemModal";
import { AddRecipientModal, RecipientForm } from "./FacilityOrderCreatePageAddRecipientModal";
import { showAddressHtml } from "./Data/Location";
import React from "react";
import { Input } from "./Controls/Input";
import { useNavigate, useRouteLoaderData } from "react-router-dom";
import { Facility, Order, OrderCustomReq } from "./Data/ApiTypes";
import { Check, Envelope, InfoCircleFill, PersonAdd, Telephone, Trash } from "react-bootstrap-icons";
import { PageBar, ToolButton } from "./Controls/PageBar";
import { showBrowserDate, showDate } from "./Data/Date";
import { LocalDate } from "@js-joda/core";
import { Api } from "./Data/Api";
import { createLocalDate } from "./Data/ApiConverters";
import { OrderCustomItemReqApi } from "./Data/ApiTransport";
import { LargePage } from "./Controls/LargePage";
import { add, multiply } from "dinero.js";
import { cents, showMoney } from "./Data/Money";
import { toast } from "react-toastify";
import "./FacilityCustomOrderCreatePage.css";

type InvoiceConfirmSummary = {
    name: string;
    email: string;
    description: string;
    dueDateLabel: string;
    itemCount: number;
    totalLabel: string;
};

function InvoiceConfirmDialog({
    show,
    summary,
    loading,
    onHide,
    onConfirm,
}: {
    show: boolean;
    summary: InvoiceConfirmSummary | null;
    loading: boolean;
    onHide: () => void;
    onConfirm: () => void;
}) {
    if (!summary) {
        return null;
    }

    return (
        <Modal show={show} onHide={onHide} centered dialogClassName="invite-confirm-modal">
            <Modal.Header closeButton className="border-0 pb-0">
                <div>
                    <Modal.Title className="invite-confirm-title">Confirm Invoice</Modal.Title>
                    <p className="invite-confirm-subtitle mb-0">
                        Review these details before sending the invoice.
                    </p>
                </div>
            </Modal.Header>
            <Modal.Body className="pt-3">
                <div className="invite-confirm-panel">
                    {([
                        ["Recipient", summary.name],
                        ["Email", summary.email],
                        ["Description", summary.description],
                        ["Due Date", summary.dueDateLabel],
                        ["Line Items", String(summary.itemCount)],
                        ["Total", summary.totalLabel],
                    ] as const).map(([label, value]) => (
                        <div key={label} className="invite-confirm-row">
                            <span className="invite-confirm-label">{label}</span>
                            <span className="invite-confirm-value">{value}</span>
                        </div>
                    ))}
                </div>
                <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-0 mt-3 small">
                    <InfoCircleFill className="flex-shrink-0 mt-1" />
                    <span>An invoice will be created and emailed to the recipient.</span>
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
                <Button type="button" onClick={onHide} disabled={loading}>
                    Go Back
                </Button>
                <Button type="button" className="btn-main" loading={loading} onClick={onConfirm}>
                    Send Invoice
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export function FacilityInvoiceCreatePage({ recipientOverride, setShowCustomOrder, onOrderCreated }: { recipientOverride?: RecipientForm,
     setShowCustomOrder?: (value: React.SetStateAction<boolean>) => void,
     onOrderCreated?: (order: Order) => void }) {
    const nav = useNavigate();
    const facility = useRouteLoaderData( "facility" ) as Facility;
    const [ showAddRec,  setShowAddRec  ] = useState( false );
    const [ showAddItem, setShowAddItem ] = useState( false );
    const [ showConfirm, setShowConfirm ] = useState( false );
    const [ confirmLoading, setConfirmLoading ] = useState( false );
    const [ confirmSummary, setConfirmSummary ] = useState<InvoiceConfirmSummary | null>( null );
    const [recipient, setRecipients] = useState<RecipientForm | undefined>(recipientOverride);
    const [ items,       setItems       ] = useState<OrderCustomItemReqApi[]>( [] );
    const [ desc,        setDesc        ] = useState( "" );
    const [ dueDate,     setDueDate     ] = useState( showBrowserDate( LocalDate.now() ) );

    const canChangeRecipient = recipientOverride === undefined;
    const totalMoney = items
        .map( i => multiply( cents( i.unitPrice ), i.quantity ) )
        .reduce( ( a, b ) => add( a, b ), cents( 0 ) );
    const dueDateDisplay = showDate( createLocalDate( dueDate ) );

    function addRecipient( r: RecipientForm ) {
        setRecipients( r );
    }

    function addItem( item: OrderCustomItemReqApi ) {
        setItems( [ ...items, item ] );
    }

    function removeItem( index: number ) {
        setItems( items.filter( ( _, i ) => i !== index ) );
    }

    const navigateBack = () => {
        if (setShowCustomOrder) {
        setShowCustomOrder(false);
        } else {
        nav(-1); 
        }
    };

    function openConfirm() {
        if( !recipient ) {
            return;
        }
        setConfirmSummary({
            name: recipient.name,
            email: recipient.emailAddress,
            description: desc,
            dueDateLabel: dueDateDisplay,
            itemCount: items.length,
            totalLabel: showMoney( totalMoney ),
        });
        setShowConfirm( true );
    }

    async function submit() {
        if( !recipient ) {
            return;
        }
        setConfirmLoading( true );
        try {
            const req: OrderCustomReq = {
                name:         recipient.name,
                emailAddress: recipient.emailAddress,
                phoneNumber:  recipient.phoneNumber,
                description:  desc,
                items:        items,
                dueDate:      createLocalDate( dueDate ),
            };
            const res = await Api.facilityCustomOrderCreate( facility.facilityId, req );
            if ( !res.isOk ) {
                toast.error( "Failed to create invoice. Please try again." );
                return;
            }
            toast.success( "Invoice sent successfully" );
            onOrderCreated?.( res.value );
            setShowConfirm( false );
            navigateBack();
        } finally {
            setConfirmLoading( false );
        }
    }

    return <LargePage>
        <AddRecipientModal show={showAddRec}  setShow={setShowAddRec}  onSave={addRecipient} />
        <AddLineItemModal  show={showAddItem} setShow={setShowAddItem} onSave={addItem}      />
        <InvoiceConfirmDialog
            show={showConfirm}
            summary={confirmSummary}
            loading={confirmLoading}
            onHide={() => { if (!confirmLoading) setShowConfirm(false); }}
            onConfirm={() => void submit()}
        />
        <PageBar   title={
                <div className="d-flex align-items-center gap-2">
                <BsButton variant="link" className="p-0 me-2" onClick={() => navigateBack()}>
                    ← Back
                </BsButton>
                Custom Order
                </div>
            } >
            {canChangeRecipient && (
                <ToolButton onClick={ () => setShowAddRec( true ) }>
                    <PersonAdd color="maroon" /> {recipient ? "Change Recipient" : "Set Recipient"}
                </ToolButton>
            )}
        </PageBar>
        <Container fluid className="g-0">
            <Row>
                <Col lg="12" xl="4">
                    <div className="mt-2">
                            <Input type="date" label="Due Date"
                                value={dueDate}
                                onChange={e => setDueDate( e.target.value )} />
                            <Input type="text" label="Short Description" placeholder="i.e. Additional Services, etc."
                                autoFocus
                                value={desc}
                                onChange={e => setDesc( e.target.value )} />
                    </div>
                </Col>
                <Col lg="12" xl="auto" className="d-flex align-items-center">
                    <ListGroup variant="flush" className="text-muted">
                        <ListGroupItem className="d-flex-align-center">
                            <Check size="20" className="text-success" />
                            Use this form to create a custom order
                        </ListGroupItem>
                        {canChangeRecipient && (
                            <ListGroupItem className="d-flex-align-center">
                                <Check size="20" className="text-success" />
                                The recipient can be an existing parker or a new one (an account will be created)
                            </ListGroupItem>
                        )}
                        <ListGroupItem className="d-flex-align-center">
                            <Check size="20" className="text-success" />
                            After you confirm, an order, invoice, and email will be generated
                        </ListGroupItem>
                    </ListGroup>
                </Col>
            </Row>
        </Container>
        <div className="p-0 border-0 p-md-3 shadow-md mt-3 rounded-0">
            <Row>
                <Col xs={6}>
                    <div className="d-flex flex-column">
                        <strong className="fs-5 text-uppercase">
                            {facility.name}
                        </strong>
                        <div>
                            {showAddressHtml( facility.address )}
                        </div>
                        <div className="mt-1 d-flex align-items-center gap-2">
                            <Envelope color="maroon" /> {facility.emailAddress}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <Telephone color="maroon" /> {facility.phoneNumber}
                        </div>
                    </div>
                </Col>
                <Col xs={6} className="text-md-end">
                    <div>
                        <span className="fw-semibold text-secondary">INVOICE</span>
                    </div>
                    <div>
                        Created: {showDate( LocalDate.now() )}
                    </div>
                    <div>
                        Due Date: {dueDateDisplay}
                    </div>
                </Col>
            </Row>
            <hr />
            <Row className="mb-4 gy-2">
                <Col lg={4}>
                    <div className="fs-4 text-secondary text-uppercase">
                        Recipient
                    </div>
                    <div className="d-flex flex-column">
                        {recipient === undefined ? "No Recipient Added" : (
                            <>
                                <div>{recipient.name}</div>
                                <div className="d-flex align-items-center gap-2">
                                    <Envelope color="maroon" /> {recipient.emailAddress}
                                </div>
                                {recipient.phoneNumber && (
                                    <div className="d-flex align-items-center gap-2">
                                        <Telephone color="maroon" /> {recipient.phoneNumber}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </Col>
                <Col lg={8}>
                    <div className="fs-4 ms-1 text-secondary text-uppercase">
                        Details
                    </div>                    
                    <div className="d-grid mt-2">
                        <div className="grid-col-1 border-bottom border-2 pb-2 mb-2">Line Item</div>
                        <div className="grid-col-2 border-bottom border-2 pb-2 mb-2 text-end">Unit Price</div>
                        <div className="grid-col-3 border-bottom border-2 pb-2 mb-2 text-end">Quantity </div>
                        <div className="grid-col-4 border-bottom border-2 pb-2 mb-2 text-end">Subtotal </div>
                        {items.length === 0 && (
                            <button
                                type="button"
                                className="custom-order-add-item custom-order-add-item-empty"
                                onClick={() => setShowAddItem(true)}
                            >
                                + Add line item
                            </button>
                        )}
                        {items.map( ( el, i ) => {
                            const subtotal = multiply( cents( el.unitPrice ), el.quantity );
                            return <React.Fragment key={i}>
                                <div className="grid-col-1">{el.name}</div>
                                <div className="grid-col-2 text-end">{showMoney( cents( el.unitPrice ))}</div>
                                <div className="grid-col-3 text-end">{el.quantity}</div>
                                <div className="grid-col-4 text-end d-flex align-items-center justify-content-end gap-2">
                                    <span>{showMoney( subtotal )}</span>
                                    <button
                                        type="button"
                                        className="custom-order-item-remove"
                                        aria-label={`Remove ${el.name}`}
                                        onClick={() => removeItem( i )}
                                    >
                                        <Trash size={16} />
                                    </button>
                                </div>
                            </React.Fragment>;
                        } )}
                    </div>
                    {items.length > 0 && (
                        <button
                            type="button"
                            className="custom-order-add-item"
                            onClick={() => setShowAddItem(true)}
                        >
                            + Add line item
                        </button>
                    )}
                    <div className="mt-2">
                        Total: {showMoney( totalMoney )}
                    </div>
                </Col>
            </Row>
           
        </div>

        <div className="d-flex gap-2 mt-3 justify-content-end">
            <Button className="w-30" onClick={ () => navigateBack() }>
                Discard
            </Button>
            <Button className="w-30 btn-main" onClick={ openConfirm } disabled={(recipient === undefined || items.length == 0 || desc.length == 0 )} >
                Send Invoice
            </Button>
        </div>
    </LargePage>;
}
